<template>
  <DialogHybrid
    v-model="open"
    :sheet-title="t(title)"
    :dialog-props="{ maxWidth: 460 }"
  >
    <v-container class="text-center pa-4 pa-md-6">
      <h3 class="d-none d-md-block text-h6 mb-2 font-weight-semibold" v-text="t(title)" />
      <p class="text-body-1 mb-4" v-text="t(message)" />

      <div class="d-flex flex-column ga-3">
        <BrandButton
          v-for="(button, i) in buttons"
          :key="i"
          v-bind="{ ...defaultProps, variant: button.variant }"
          @click="button.action"
        >
          {{ t(button.text) }}
        </BrandButton>
      </div>
    </v-container>
  </DialogHybrid>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DialogHybrid from '@/components/global/DialogHybrid.vue'
import BrandButton from '@/components/global/brand/BrandButton.vue'
import { useRatingDialog } from '@/composables/useRatingPrompt'
import { RATING_RESULT, RATING_STEP } from '@/constants/ratingPrompt'

const { t } = useI18n()
const { dialogOpen, settleRatingDialog, notifyRatingDialogDisplayed } = useRatingDialog()

// Two-step soft-ask: the question, then a "sorry to hear that" follow-up that
// offers to collect feedback instead. Only step 2 can answer negatively —
// "not really" on step 1 just advances.
const step = ref(RATING_STEP.ASK)
const isAsk = computed(() => step.value === RATING_STEP.ASK)

const title = computed(() => (isAsk.value ? 'rating.softask.title' : 'rating.feedback.title'))
const message = computed(() => (isAsk.value ? 'rating.softask.message' : 'rating.feedback.message'))

// Positive / primary action first.
const buttons = computed(() => {
  if (isAsk.value) {
    return [
      { variant: 'primary', text: 'rating.softask.yes', action: () => settleRatingDialog(RATING_RESULT.POSITIVE) },
      { variant: 'basic', text: 'rating.softask.no', action: () => { step.value = RATING_STEP.FEEDBACK } },
    ]
  }

  return [
    { variant: 'primary', text: 'rating.feedback.yes', action: () => settleRatingDialog(RATING_RESULT.FEEDBACK) },
    { variant: 'basic', text: 'rating.feedback.no', action: () => settleRatingDialog(RATING_RESULT.NEGATIVE) },
  ]
})

// flush: 'post' so this runs AFTER the DOM patch — the point is that the dialog
// really reached the screen, not that something asked for it. That ack is what
// spends one of the allowed shows.
watch(dialogOpen, isOpen => {
  if (!isOpen) return
  step.value = RATING_STEP.ASK
  notifyRatingDialogDisplayed()
}, { immediate: true, flush: 'post' })

// Two-way binding to the shared open flag. Setting false (tap-outside / back /
// swipe-down) counts as a dismissal -> null result.
const open = computed({
  get: () => dialogOpen.value,
  set: val => { if (!val) settleRatingDialog(null) },
})

const defaultProps = { size: 'x-large', block: true }
</script>
